import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { type OptionProps, type OptionType, CheckboxOption, PopupSelect } from '@atlaskit/select';
import { useIntl } from '@atlassian/jira-intl';
import type { repositoryFilter_softwareCodePage_RepositoriesFilter$key } from '@atlassian/jira-relay/src/__generated__/repositoryFilter_softwareCodePage_RepositoriesFilter.graphql';
import { REPOSITORIES_FILTER_BUTTON_TEST_ID } from '../../../../../../../common/constants';
import { FilterButton } from '../../../../../../../common/ui/filter-button';
import { UNKNOWN_ID } from '../../../../../../../common/utils/filters/constants';
import { distinct } from '../../../../../../../common/utils/filters/utils';
import { usePullRequestTableOptionsStore } from '../../../../../../../controllers/pull-request-filters';
import messages from './messages';

type RepositoriesFilterProps = {
	disabled: boolean;
	repositoriesFilterData: repositoryFilter_softwareCodePage_RepositoriesFilter$key;
};

export const RepositoriesFilter = ({
	disabled,
	repositoriesFilterData,
}: RepositoriesFilterProps) => {
	const data = useFragment(
		graphql`
			fragment repositoryFilter_softwareCodePage_RepositoriesFilter on DevOpsPullRequestDetails
			@relay(plural: true) {
				repositoryId
				repositoryName
			}
		`,
		repositoriesFilterData,
	);

	const { formatMessage } = useIntl();
	const options: OptionType[] = useMemo(
		() =>
			distinct(data, (item) => item?.repositoryId).map((pullRequest) => ({
				label: pullRequest?.repositoryName ?? formatMessage(messages.unknownRepository),
				value: pullRequest?.repositoryId ?? UNKNOWN_ID,
				data: {},
			})),
		[data, formatMessage],
	);

	const defaults = {
		options,
		placeholder: formatMessage(messages.searchLabel),
		'aria-label': formatMessage(messages.searchLabel),
		components: {
			Option: (props: OptionProps<OptionType, true>) => (
				<CheckboxOption {...props}>{props.children}</CheckboxOption>
			),
		},
	};

	const [{ repositoryFilter }, { setRepositoryFilter }] = usePullRequestTableOptionsStore();
	const selectedValues = useMemo(
		() =>
			repositoryFilter
				.map((repositoryId) => options.find((option) => option.value === repositoryId))
				.filter((option): option is OptionType => option != null),
		[repositoryFilter, options],
	);

	return (
		<PopupSelect
			{...defaults}
			value={selectedValues}
			backspaceRemovesValue
			controlShouldRenderValue
			isClearable
			closeMenuOnSelect={false}
			hideSelectedOptions={false}
			isDisabled={disabled}
			// Temporary strategy to mitigate a PopupSelect regression causing scroll jumps
			popperProps={{ strategy: 'fixed' }}
			isMulti
			classNamePrefix="repository-filter"
			onChange={(selectedOptions) =>
				setRepositoryFilter(
					selectedOptions
						.map((option) => option.value)
						.filter((value): value is string => typeof value === 'string'),
				)
			}
			target={({ isOpen, ...triggerProps }) => (
				<FilterButton
					{...triggerProps}
					testId={REPOSITORIES_FILTER_BUTTON_TEST_ID}
					isSelected={isOpen}
					label={formatMessage(messages.repositorySelectLabel)}
					selectedCount={repositoryFilter.length}
				/>
			)}
		/>
	);
};
